import type {
  ActionArtKey,
  ActionStripAsset,
  ActionStripRegistry,
} from './registry';

export const ACTION_ART_MAX_DECODED_STRIPS = 2;
export const ACTION_ART_IDLE_EVICTION_MS = 30_000;
export const ACTION_ART_FAILURE_RETRY_MS = 30_000;

export type ActionStripDecoder<TImage> = (
  asset: ActionStripAsset,
  signal: AbortSignal,
) => Promise<TImage>;

export interface ReadyActionStrip<TImage> {
  readonly status: 'ready';
  readonly asset: ActionStripAsset;
  readonly image: TImage;
  acquirePin(): ActionStripPin;
}

export interface ActionStripPin {
  readonly released: boolean;
  release(): void;
}

export interface PendingActionStrip {
  readonly status: 'pending';
}

export interface FailedActionStrip {
  readonly status: 'failed';
  readonly error: unknown;
}

export type ActionStripStatus<TImage> =
  | ReadyActionStrip<TImage>
  | PendingActionStrip
  | FailedActionStrip;

export interface ActionArtWarmRoles {
  readonly selected?: ActionArtKey;
  readonly ai?: ActionArtKey;
}

export interface ActionArtRuntime<TImage> {
  lookupForCue(cueToken: string | number, key: ActionArtKey): ActionStripStatus<TImage>;
  warmRoles(roles: ActionArtWarmRoles): void;
  abortPending(): void;
  dispose(): void;
}

export interface ActionArtRuntimeOptions<TImage> {
  readonly registry: ActionStripRegistry;
  readonly decoder: ActionStripDecoder<TImage>;
  readonly releaseImage?: (image: TImage) => void;
}

interface ReadyEntry<TImage> {
  readonly asset: ActionStripAsset;
  readonly image: TImage;
  lastAccessAt: number;
  lastAccessOrder: number;
  evictionTimer?: ReturnType<typeof setTimeout>;
  pinCount: number;
  releaseRequested: boolean;
  resourceReleased: boolean;
}

interface FailureEntry {
  readonly error: unknown;
  readonly failedAtMs: number;
}

interface DecodeJob {
  readonly id: number;
  readonly key: ActionArtKey;
  readonly generation: number;
  readonly controller: AbortController;
}

const PENDING_STATUS: PendingActionStrip = Object.freeze({ status: 'pending' });
const DISPOSED_ERROR = new Error('Action art runtime has been disposed.');

/**
 * A cue-facing action-art cache. Lookups never await: a miss returns `pending`
 * so the director can keep one clone-based fallback for the complete cue.
 */
export function createActionArtRuntime<TImage>(
  options: ActionArtRuntimeOptions<TImage>,
): ActionArtRuntime<TImage> {
  const ready = new Map<ActionArtKey, ReadyEntry<TImage>>();
  const failures = new Map<ActionArtKey, FailureEntry>();
  const jobsByKey = new Map<ActionArtKey, DecodeJob>();
  const queue: DecodeJob[] = [];
  let inFlight: DecodeJob | undefined;
  let activeCueToken: string | number | undefined;
  let hasActiveCue = false;
  let generation = 0;
  let nextJobId = 0;
  let accessOrder = 0;
  let disposed = false;

  function release(image: TImage): void {
    try {
      options.releaseImage?.(image);
    } catch {
      // Resource cleanup must not destabilize cue presentation.
    }
  }

  function releaseEntryResource(entry: ReadyEntry<TImage>): void {
    if (entry.resourceReleased) return;
    entry.resourceReleased = true;
    release(entry.image);
  }

  function evict(key: ActionArtKey, expected?: ReadyEntry<TImage>): void {
    const entry = ready.get(key);
    if (entry === undefined || (expected !== undefined && entry !== expected)) return;
    if (entry.evictionTimer !== undefined) clearTimeout(entry.evictionTimer);
    entry.evictionTimer = undefined;
    ready.delete(key);
    if (entry.pinCount > 0) {
      entry.releaseRequested = true;
    } else {
      releaseEntryResource(entry);
    }
  }

  function scheduleIdleEviction(key: ActionArtKey, entry: ReadyEntry<TImage>): void {
    if (entry.evictionTimer !== undefined) clearTimeout(entry.evictionTimer);
    entry.evictionTimer = undefined;
    if (entry.pinCount > 0) return;
    const remaining = Math.max(
      0,
      ACTION_ART_IDLE_EVICTION_MS - (Date.now() - entry.lastAccessAt),
    );
    entry.evictionTimer = setTimeout(() => {
      const current = ready.get(key);
      if (current !== entry) return;
      current.evictionTimer = undefined;
      if (current.pinCount > 0) return;
      const idleFor = Date.now() - current.lastAccessAt;
      if (idleFor >= ACTION_ART_IDLE_EVICTION_MS) {
        evict(key, current);
      } else {
        scheduleIdleEviction(key, current);
      }
    }, remaining);
  }

  function touch(key: ActionArtKey, entry: ReadyEntry<TImage>): void {
    entry.lastAccessAt = Date.now();
    entry.lastAccessOrder = ++accessOrder;
    scheduleIdleEviction(key, entry);
  }

  function enforceLruLimit(protectedKey?: ActionArtKey): void {
    while (ready.size > ACTION_ART_MAX_DECODED_STRIPS) {
      let oldest: { readonly key: ActionArtKey; readonly entry: ReadyEntry<TImage> } | undefined;
      for (const [key, entry] of ready) {
        if (key === protectedKey || entry.pinCount > 0) continue;
        if (oldest === undefined || entry.lastAccessOrder < oldest.entry.lastAccessOrder) {
          oldest = { key, entry };
        }
      }
      if (oldest === undefined) return;
      evict(oldest.key, oldest.entry);
    }
  }

  function settleJob(job: DecodeJob): void {
    if (jobsByKey.get(job.key) === job) jobsByKey.delete(job.key);
    if (inFlight === job) inFlight = undefined;
    pumpQueue();
  }

  function attachDecoded(job: DecodeJob, image: TImage): void {
    if (disposed || job.controller.signal.aborted || job.generation !== generation) {
      release(image);
      return;
    }

    const entry: ReadyEntry<TImage> = {
      asset: options.registry[job.key],
      image,
      lastAccessAt: Date.now(),
      lastAccessOrder: ++accessOrder,
      pinCount: 0,
      releaseRequested: false,
      resourceReleased: false,
    };
    ready.set(job.key, entry);
    scheduleIdleEviction(job.key, entry);
    enforceLruLimit(job.key);
  }

  function startJob(job: DecodeJob): void {
    inFlight = job;
    let decoding: Promise<TImage>;
    try {
      decoding = options.decoder(options.registry[job.key], job.controller.signal);
    } catch (error) {
      if (
        !disposed
        && !job.controller.signal.aborted
        && job.generation === generation
      ) {
        failures.set(job.key, { error, failedAtMs: Date.now() });
      }
      settleJob(job);
      return;
    }
    void decoding.then(
      (image) => attachDecoded(job, image),
      (error: unknown) => {
        if (
          !disposed
          && !job.controller.signal.aborted
          && job.generation === generation
        ) {
          failures.set(job.key, { error, failedAtMs: Date.now() });
        }
      },
    ).finally(() => settleJob(job));
  }

  function pumpQueue(): void {
    if (disposed || inFlight !== undefined) return;
    let job = queue.shift();
    while (job !== undefined) {
      if (
        jobsByKey.get(job.key) === job
        && !job.controller.signal.aborted
        && job.generation === generation
      ) {
        startJob(job);
        return;
      }
      job = queue.shift();
    }
  }

  function request(key: ActionArtKey): ActionStripStatus<TImage> {
    if (disposed) return { status: 'failed', error: DISPOSED_ERROR };

    const decoded = ready.get(key);
    if (decoded !== undefined) {
      touch(key, decoded);
      return {
        status: 'ready',
        asset: decoded.asset,
        image: decoded.image,
        acquirePin: () => {
          decoded.pinCount += 1;
          touch(key, decoded);
          let pinReleased = false;
          return {
            get released() {
              return pinReleased;
            },
            release: () => {
              if (pinReleased) return;
              pinReleased = true;
              decoded.pinCount = Math.max(0, decoded.pinCount - 1);
              if (decoded.pinCount > 0) return;
              if (decoded.releaseRequested) {
                releaseEntryResource(decoded);
                return;
              }
              if (Date.now() - decoded.lastAccessAt >= ACTION_ART_IDLE_EVICTION_MS) {
                evict(key, decoded);
              } else {
                scheduleIdleEviction(key, decoded);
                enforceLruLimit();
              }
            },
          };
        },
      };
    }
    const failure = failures.get(key);
    if (failure !== undefined) {
      if (Date.now() - failure.failedAtMs < ACTION_ART_FAILURE_RETRY_MS) {
        return { status: 'failed', error: failure.error };
      }
      failures.delete(key);
    }
    if (!jobsByKey.has(key)) {
      const job: DecodeJob = {
        id: ++nextJobId,
        key,
        generation,
        controller: new AbortController(),
      };
      jobsByKey.set(key, job);
      queue.push(job);
      pumpQueue();
    }
    return PENDING_STATUS;
  }

  function cancelPending(): void {
    generation += 1;
    inFlight?.controller.abort();
    for (const job of queue) job.controller.abort();
    queue.length = 0;
    jobsByKey.clear();
  }

  function lookupForCue(
    cueToken: string | number,
    key: ActionArtKey,
  ): ActionStripStatus<TImage> {
    if (!hasActiveCue || activeCueToken !== cueToken) {
      if (hasActiveCue) cancelPending();
      activeCueToken = cueToken;
      hasActiveCue = true;
    }
    return request(key);
  }

  function warmRoles(roles: ActionArtWarmRoles): void {
    const keys = [roles.selected, roles.ai].filter(
      (key): key is ActionArtKey => key !== undefined,
    );
    for (const key of new Set(keys)) request(key);
  }

  function abortPending(): void {
    if (disposed) return;
    cancelPending();
    activeCueToken = undefined;
    hasActiveCue = false;
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    cancelPending();
    for (const [key, entry] of ready) evict(key, entry);
    failures.clear();
    activeCueToken = undefined;
    hasActiveCue = false;
  }

  return Object.freeze({ lookupForCue, warmRoles, abortPending, dispose });
}
